import { useState } from "react";
import {
  Button,
  TextField,
  Box,
  Typography,
  Paper,
  IconButton,
  InputAdornment,
  Link,
  useTheme
} from "@mui/material";
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import { useNavigate } from 'react-router-dom';

import api from '../services/api.js';

export default function Login() {
  const [email, setEmail] = useState("");
  const [senha, setSenha] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const theme = useTheme();
  const navigate = useNavigate();

  // Removido useEffect que causava loop infinito
  // O App.jsx já faz a verificação de usuário e redirecionamento

  const handleClickShowPassword = () => setShowPassword((show) => !show);
  const handleMouseDownPassword = (event) => {
    event.preventDefault();
  };

    const handleLogin = async () => {
    setError(""); // Limpa erros anteriores
    
    if (!email || !senha) {
      setError("Preencha o e-mail e a senha corretamente.");
      return;
    }

    try {
      // ✅ Usar API centralizada com axios
      const res = await api.post('/login', {
        st_email: email,
        st_senha: senha,
      });

      const data = res.data;

      const modulosPermitidos = (Array.isArray(data.usuario?.modulos) ? data.usuario.modulos : []).map((modulo) => {
        const nome = modulo.nome || modulo.st_nome;
        const descricao = modulo.descricao || modulo.st_descricao || nome;
        const icone = modulo.icone || modulo.st_icone;

        return {
          ...modulo,
          nome,
          st_nome: nome,
          descricao,
          st_descricao: descricao,
          icone,
          st_icone: icone,
          path: nome ? `/${nome}` : undefined
        };
      });
      const usuarioComModulos = {
        ...data.usuario,
        modulos: modulosPermitidos
      };

      localStorage.setItem("token", data.token);
      localStorage.setItem("refreshToken", data.refreshToken);
      localStorage.setItem("usuario", JSON.stringify(usuarioComModulos));
      localStorage.setItem("st_codHashEmpresa", data.usuario.st_codHashEmpresa);
      localStorage.setItem("modulos", JSON.stringify(modulosPermitidos));

      const primeiraRotaPermitida = modulosPermitidos.find((modulo) => modulo.path)?.path || "/dashboard";
      window.location.href = primeiraRotaPermitida;
    } catch (err) {
      console.error("Erro de conexão:", err);
      if (err.response?.data?.error) {
        setError(err.response.data.error);
      } else {
        setError("Erro de conexão. Verifique sua internet e tente novamente.");
      }
    }
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    handleLogin();
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
        padding: 2,
        position: 'relative'
      }}
    >
      <Paper
        elevation={8}
        sx={{
          padding: 4,
          borderRadius: 3,
          maxWidth: 400,
          width: '100%',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
      >
        <LockOutlinedIcon sx={{ fontSize: 60, color: theme.palette.primary.main, mb: 2 }} />
        <Typography variant="h5" component="h1" gutterBottom>
          AuthSystem
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Bem-vindo de volta! Faça login para continuar.
        </Typography>

        <Box component="form" onSubmit={handleSubmit} noValidate sx={{ width: '100%' }}>
          <TextField
            margin="normal"
            fullWidth
            id="email"
            label="E-mail"
            name="email"
            autoComplete="email"
            autoFocus
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            error={!!error}
          />
          <TextField
            margin="normal"
            fullWidth
            name="password"
            label="Senha"
            type={showPassword ? 'text' : 'password'}
            id="password"
            autoComplete="current-password"
            value={senha}
            onChange={(e) => setSenha(e.target.value)}
            error={!!error}
            helperText={error}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    aria-label="toggle password visibility"
                    onClick={handleClickShowPassword}
                    onMouseDown={handleMouseDownPassword}
                    edge="end"
                  >
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
          <Link 
            component="button"
            variant="body2" 
            sx={{ mt: 1, display: 'block', textAlign: 'right', cursor: 'pointer' }}
            onClick={() => navigate('/forgot-password')}
          >
            Esqueceu sua senha?
          </Link>
          <Button
            type="submit"
            fullWidth
            variant="contained"
            color="primary"
            sx={{ mt: 3, mb: 2, py: 1.5 }}
          >
            Entrar
          </Button>
        </Box>
        <Typography variant="body2" color="text.secondary">
          Não tem uma conta?{' '}
          <Link 
            component="button"
            variant="body2" 
            onClick={() => navigate('/contact')}
            sx={{ fontWeight: 'bold', cursor: 'pointer' }}
          >
            Cadastre-se aqui
          </Link>
        </Typography>
      </Paper>
      
      {/* Logo no canto inferior direito */}
      <Box sx={{
        position: 'fixed',
        right: 0,
        bottom: 0,
        width: '400px',
        height: '300px',
        zIndex: 1,
        backgroundImage: 'url(/logo_canto.gif)',
        backgroundSize: 'contain',
        backgroundRepeat: 'no-repeat',
        backgroundPosition: 'bottom right',
        opacity: 0.25,
        pointerEvents: 'none'
      }} />
      
      {/* Rodapé */}
      <Box sx={{
        position: 'fixed',
        bottom: 0,
        left: 0,
        right: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        color: 'white',
        textAlign: 'center',
        padding: '12px',
        fontSize: '0.875rem',
        zIndex: 2
      }}>
        <Typography variant="body2">
          Desenvolvido por SAOTech Software. Tel. (11) 9 2039-2120 - suporte@saoinformatica.com.br
        </Typography>
      </Box>
    </Box>
  );
}
